//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@banno/jha-wc/src/tables/jha-table/jha-table";
import "@banno/jha-wc/src/tables/jha-table-row/jha-table-row";
import "@banno/jha-wc/src/tables/jha-table-header/jha-table-header";
import "@banno/jha-wc/src/tables/jha-table-cell/jha-table-cell";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/icons/jha-icon-plus";
import "@banno/jha-wc/src/icons/jha-icon-edit";
import "@banno/jha-wc/src/icons/jha-icon-delete";
import "@banno/jha-wc/src/icons/jha-icon-show";
import "@banno/jha-wc/src/icons/jha-icon-warning-filled";
import "@banno/jha-wc/src/icons/jha-icon-information";
import "@banno/jha-wc/src/jha-tooltip/jha-tooltip";
import "@banno/jha-wc/src/forms/jha-form-checkbox/jha-form-checkbox";
import DialogUtil from "@banno/jha-wc/src/js/utils/dialog-util";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import { AddWorkflowStepPopup } from "../Workflow/AddWorkflowStepPopup";
import { stringComparison, showToastMessage } from "../../utils/app-utils";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";
import { stepToPopupFormat, normalizeStep } from "../../utils/api-normalizer";
import { getStepFunctionDisplayLabel } from "../../utils/step-function-display";

/**
 * Step interface for internal use
 */
interface TemplateStep {
  stepIndex: number;
  stepName: string;
  functionName: string;
  name?: string;
  path?: string;
  locationType?: string;
  storageType?: string;
  deleteSourceFile?: string | boolean;
  fileRename?: any;
  bucketRename?: string;
  secretName?: string;
  secretValue?: any;
  FileMovePattern?: string[];
  // SFTP/FTPS fields
  HostName?: string;
  Port?: string | number;
  UserName?: string;
  Password?: string;
  // Encryption fields
  PGPKey?: string;
  // Azure fields
  ConnectionString?: string;
  // AWS fields
  RoleARN?: string;
  Region?: string;
  isValidStep?: boolean;
}

/**
 * Workflow Template Steps View
 *
 * A step management component specifically designed for workflow templates.
 * Features action icons matching the Variables and Notifications grids.
 *
 * Features:
 * - Add new steps using existing popup dialogs
 * - Edit existing steps
 * - Delete steps with confirmation
 * - View-only mode support
 * - Professional empty state display
 */
@customElement("workflow-template-steps-view")
export class WorkflowTemplateStepsView extends formElementMixin(LitElement) {
  static get is() {
    return "workflow-template-steps-view";
  }

  @property({ type: Array }) steps: TemplateStep[] = [];
  @property({ type: Boolean }) isReadOnly: boolean = false;
  @property({ type: String }) transferType: string = "";
  @property({ type: Boolean }) deleteFiles: boolean = true;
  @property({ type: Object }) formData: any = {};

  /** Track when step popup is open (for midEdit state) */
  @state() isStepPopupOpen: boolean = false;

  // ============================================
  // TEMPLATE VARIABLE SUPPORT
  // These properties enable template-string-input components in the
  // AddWorkflowStepPopup dialog, allowing users to insert template variables like
  // {businessUnit} into step configuration fields.
  // ============================================

  /**
   * Template variables available for use in step fields.
   * Passed to AddWorkflowStepPopup to populate the variable dropdown.
   */
  @property({ type: Array }) variables: WorkflowTemplateVariable[] = [];

  /**
   * [ACME-1422] Friendly Business Unit display name (e.g. "JHFT Testing")
   * forwarded to `AddWorkflowStepPopup` so the SMB bucket preview matches
   * the `{env}-smb-{slug}` shape the API will persist. Resolved by the parent
   * (WorkflowTemplateStepsTab) from the Settings tab's selected BU; empty for
   * Global templates where no BU is bound at template-author time.
   */
  @property({ type: String }) buDisplayName: string = "";

  /**
   * Handle variable-added event from AddWorkflowStepPopup.
   * When a user creates a new variable inline within the popup,
   * this propagates the event up to WorkflowTemplateGeneralView
   * where it's added to the Settings tab's variable list.
   */
  onVariableAdded(e: CustomEvent) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("variable-added", {
        detail: e.detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Dispatch midEdit state change to parent.
   * Called when step popup opens (midEdit=true) or closes (midEdit=false).
   */
  private dispatchMidEditChange(midEdit: boolean) {
    this.isStepPopupOpen = midEdit;
    this.dispatchEvent(
      new CustomEvent("onStepMidEditChange", {
        detail: { midEdit },
        bubbles: true,
        composed: true,
      })
    );
  }

  static readonly styles = css`
    :host {
      display: block;
    }

    /* Table styling - matching Variables/Notifications grid */
    jha-table {
      width: 100%;
      border-collapse: collapse;
    }

    jha-table-header {
      font-weight: 600;
      padding: 12px;
      text-align: left;
      white-space: nowrap;
      background: var(--jh-color-container-primary-enabled, rgb(244, 244, 244));
      border-top: 1px solid var(--jh-color-divider-primary);
    }

    jha-table-cell {
      padding: 12px;
      vertical-align: middle;
      border-left: 1px solid var(--jha-advanced-table-cell-border);
      font-size: 15px;
      line-height: 1.4;
    }
    
    /* Center the actions column cells */
    jha-table-cell:last-child {
      text-align: center;
    }

    jha-table-row {
      border-bottom: 1px solid;
    }

    /* Action icons - matching Variables/Notifications grid */
    .action-icons {
      display: flex;
      gap: 12px;
      align-items: center;
      justify-content: center;
    }

    .action-icon {
      cursor: pointer;
      width: 20px;
      height: 20px;
      color: var(--jha-color-primary, #0066cc);
      transition: opacity 0.2s;
    }

    .action-icon:hover {
      opacity: 0.7;
    }

    .action-icon[disabled] {
      cursor: not-allowed;
      opacity: 0.3;
    }

    jha-icon-edit,
    jha-icon-show {
      color: var(--jha-color-primary, #0066cc);
    }

    jha-icon-delete {
      color: var(--jha-color-danger, #dc3545);
    }

    jha-icon-warning-filled {
      fill: var(--jha-color-warning);
      margin-left: 5px;
    }

    #addStepContainer {
      margin-top: 16px;
      display: flex;
      justify-content: flex-end;
    }

    .step-index-cell {
      display: flex;
      align-items: center;
    }

    .cell-display {
      padding: 0;
      word-break: break-word;
    }

    /* Checkbox section for Push type */
    .delete-files-section {
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .delete-files-section p {
      margin: 0;
      font-size: 14px;
    }

    .info-tooltip {
      position: relative;
    }

    #filePatternTooltip {
      position: absolute;
      border-radius: 4px;
      padding: 8px;
      width: 500px;
      background: black;
      color: white;
      box-sizing: border-box;
      z-index: 1061;
      display: none;
    }

    .iconInfo:hover + #filePatternTooltip {
      display: block;
    }

    /* Empty state message - matching Variables/Notifications grid */
    .no-steps-message {
      padding: 32px;
      text-align: center;
      border: 1px solid;
      border-top: none;
    }

    /* Light mode - matches Variables/Notifications grid styling */
    @media (prefers-color-scheme: light) {
      .no-steps-message {
        color: #666666;
        border-color: #e0e0e0;
      }

      jha-table-row {
        border-bottom-color: #e0e0e0;
      }
    }

    /* Dark mode - matches Variables/Notifications grid styling */
    @media (prefers-color-scheme: dark) {
      .no-steps-message {
        color: #a0a0a0;
        border-color: #3a3a3a;
      }

      jha-table {
        border: 1px solid #3a3a3a;
      }

      jha-table-row {
        border-bottom-color: #3a3a3a;
      }
    }
  `;

  /**
   * Check if transfer type is Push
   */
  get isPushType(): boolean {
    return stringComparison(this.transferType, "push");
  }

  /**
   * Handle delete files checkbox change
   */
  onDeleteFilesChange(e: CustomEvent) {
    this.deleteFiles = e.detail.checked;
    this.dispatchStepsChange();
  }

  /**
   * Add a new step using the existing popup dialog
   */
  async onAddStep() {
    try {
      // Signal that popup is opening (midEdit = true)
      this.dispatchMidEditChange(true);

      // Check if any existing step has deleteSourceFile enabled
      let lastDelete = false;
      if (this.steps.length > 0) {
        this.steps.forEach((element) => {
          if (
            (stringComparison(element.deleteSourceFile, "true") || element.deleteSourceFile === true) &&
            !stringComparison(element.functionName, "UnprocessedFileArchive") &&
            !stringComparison(element.functionName, "FileDelete")
          ) {
            lastDelete = true;
          }
        });
      }

      // Create popup with template variable support enabled.
      // TEMPLATE VARIABLE SUPPORT: Pass useTemplateVariables=true and the variables array
      // so that text fields in the popup render as template-string-input components,
      // allowing users to insert template variables like {businessUnit} into step configurations.
      const popup = new AddWorkflowStepPopup(
        this.transferType,
        false,
        lastDelete,
        this.transferType,
        this.formData.isClone,
        this.formData.workflowName || "",
        this.formData.BusinessUnitParticipant || "", // PascalCase to match StepsTab definition
        false, // isViewOnly
        true, // useTemplateVariables - enable template variable support for Workflow Templates
        this.variables, // variables array - available template variables for selection
        this.buDisplayName // [ACME-1422] friendly BU display name for SMB bucket preview
      );

      // Listen for variable-added events from the popup.
      // When a user creates a new variable inline, propagate it up to be added to the Settings tab.
      popup.addEventListener("variable-added", (e: CustomEvent) => {
        this.onVariableAdded(e);
      });

      const data = await DialogUtil.awaitDialog({
        dialog: popup,
      });

      // Signal that popup is closed (midEdit = false)
      this.dispatchMidEditChange(false);

      // Validate step data before adding
      if (!data.stepName?.trim() || !data.functionName?.trim()) {
        showToastMessage("Step name and function are required.", "danger");
        return;
      }

      // CASING FIX: Normalize step data from popup (PascalCase) to camelCase for storage.
      // This ensures consistent casing when getFormData() extracts values for API save.
      const normalizedData = normalizeStep(data);

      // Assign step index
      const newStepIndex = this.steps.length + 1;
      normalizedData.stepIndex = newStepIndex;

      this.steps = [...this.steps, normalizedData as any];
      this.dispatchStepsChange();
    } catch (e) {
      // User cancelled the dialog - still need to signal popup closed
      this.dispatchMidEditChange(false);
    }
  }

  /**
   * Edit an existing step using the popup dialog
   */
  async onEditStep(step: TemplateStep, isViewOnly: boolean = false) {
    const saveStepIndex = step.stepIndex;

    try {
      // Signal that popup is opening (midEdit = true) - but not for view-only
      if (!isViewOnly) {
        this.dispatchMidEditChange(true);
      }

      // Check deleteSourceFile status for other steps
      let lastDelete = false;
      let stepWithDelete: number | undefined;

      if (this.steps.length > 0) {
        this.steps.forEach((element) => {
          if (
            (stringComparison(element.deleteSourceFile, "true") || element.deleteSourceFile === true) &&
            !stringComparison(element.functionName, "UnprocessedFileArchive") &&
            !stringComparison(element.functionName, "FileDelete")
          ) {
            stepWithDelete = element.stepIndex;
          }
        });

        if (stringComparison(step.functionName, "UnprocessedFileArchive") || stringComparison(step.functionName, "FileDelete")) {
          lastDelete = false;
        } else if (step.stepIndex !== stepWithDelete && stepWithDelete !== undefined) {
          lastDelete = true;
        }
      }

      // Create popup with template variable support enabled.
      // TEMPLATE VARIABLE SUPPORT: Pass useTemplateVariables=true and the variables array
      // so that text fields in the popup render as template-string-input components,
      // allowing users to insert template variables like {businessUnit} into step configurations.
      //
      // CASING FIX: Convert step to popup format (PascalCase for SFTP/FTPS/Azure/AWS fields).
      // The popup expects PascalCase (HostName, Port, etc.) but API data uses camelCase.
      const stepForPopup = stepToPopupFormat(step, this.transferType);

      const popup = new AddWorkflowStepPopup(
        stepForPopup,
        true,
        lastDelete,
        this.transferType,
        this.formData.isClone,
        this.formData.workflowName || "",
        this.formData.BusinessUnitParticipant || "", // PascalCase to match StepsTab definition
        isViewOnly,
        true, // useTemplateVariables - enable template variable support for Workflow Templates
        this.variables, // variables array - available template variables for selection
        this.buDisplayName // [ACME-1422] friendly BU display name for SMB bucket preview
      );

      // Listen for variable-added events from the popup.
      // When a user creates a new variable inline, propagate it up to be added to the Settings tab.
      popup.addEventListener("variable-added", (e: CustomEvent) => {
        this.onVariableAdded(e);
      });

      const data = await DialogUtil.awaitDialog({
        dialog: popup,
      });

      // Signal that popup is closed (midEdit = false)
      if (!isViewOnly) {
        this.dispatchMidEditChange(false);
      }

      // Validate step data before saving edit
      if (!data.stepName?.trim() || !data.functionName?.trim()) {
        showToastMessage("Step name and function are required.", "danger");
        return;
      }

      // CASING FIX: Normalize step data from popup (PascalCase) to camelCase for storage.
      // This ensures consistent casing when getFormData() extracts values for API save.
      const normalizedData = normalizeStep(data);
      normalizedData.stepIndex = saveStepIndex;


      // Replace the edited step
      const updatedSteps = this.steps.filter((s) => s.stepIndex !== step.stepIndex);
      updatedSteps.push(normalizedData as any);
      updatedSteps.sort((a, b) => a.stepIndex - b.stepIndex);

      this.steps = updatedSteps;
      this.dispatchStepsChange();
    } catch (e) {
      // User cancelled the dialog - still need to signal popup closed
      if (!isViewOnly) {
        this.dispatchMidEditChange(false);
      }
    }
  }

  /**
   * Delete a step with confirmation (ACME-1050 — always prompt for destructive).
   */
  async onDeleteStep(step: TemplateStep) {
    const result = await openJhftDialog({
      variant: "danger",
      title: "Delete Workflow Step",
      message: "Are you sure you want to delete this workflow step?",
      cancelLabel: "Cancel",
      confirmLabel: "Delete",
    });
    if (result !== "confirm") {
      return;
    }

    // Remove the step and re-index remaining steps
    const updatedSteps = this.steps
      .filter((s) => s.stepIndex !== step.stepIndex)
      .map((s, index) => ({
        ...s,
        stepIndex: index + 1,
      }));

    this.steps = updatedSteps;
    this.dispatchStepsChange();

    showToastMessage("Workflow step deleted.", "success");
  }

  /**
   * Dispatch steps change event to parent
   */
  dispatchStepsChange() {
    this.dispatchEvent(
      new CustomEvent("onStepsChange", {
        detail: {
          workflowStepsData: this.steps,
          deleteFiles: this.deleteFiles,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Render table header
   */
  renderTableHeader(canEdit: boolean) {
    return html`
      <jha-table-row>
        <jha-table-header style="width: 60px;">NO.</jha-table-header>
        <jha-table-header style="min-width: 200px;">STEP NAME</jha-table-header>
        <jha-table-header style="min-width: 200px;">STEP FUNCTION</jha-table-header>
        <jha-table-header style="width: 120px; text-align: center;">ACTIONS</jha-table-header>
      </jha-table-row>
    `;
  }

  /**
   * Render a single step row
   */
  renderStepRow(step: TemplateStep, canEdit: boolean) {
    return html`
      <jha-table-row>
        <jha-table-cell>
          <span class="step-index-cell">
            ${step.stepIndex} ${step.isValidStep === false ? html`<jha-icon-warning-filled icon></jha-icon-warning-filled>` :
        ""}
          </span>
        </jha-table-cell>
        <jha-table-cell>
          <span class="cell-display">${step.stepName}</span>
        </jha-table-cell>
        <jha-table-cell>
          <span class="cell-display">${getStepFunctionDisplayLabel(step.functionName)}</span>
        </jha-table-cell>
        <jha-table-cell>
          <div class="action-icons">
            ${this.isReadOnly
        ? html`
            <jha-tooltip left text="View Step">
              <jha-icon-show class="action-icon" data-auto-id="stepViewIcon" @click=${() => this.onEditStep(step, true)}></jha-icon-show>
            </jha-tooltip>
            `
        : html`
            <jha-tooltip left text="Edit Step">
              <jha-icon-edit class="action-icon" data-auto-id="stepEditIcon" @click=${() => this.onEditStep(step, false)}></jha-icon-edit>
            </jha-tooltip>
            <jha-tooltip left text="Delete Step">
              <jha-icon-delete class="action-icon" data-auto-id="stepDeleteIcon" @click=${() => this.onDeleteStep(step)}></jha-icon-delete>
            </jha-tooltip>
            `}
          </div>
        </jha-table-cell>
      </jha-table-row>
    `;
  }

  render() {
    const canEdit = !this.isReadOnly;
    const hasSteps = this.steps.length > 0;

    return html`
      <div>
        <!-- Delete files checkbox for Push type -->
        ${this.isPushType
        ? html`
        <div class="delete-files-section">
          <jha-form-checkbox data-id="deleteSourceFile" data-auto-id="deleteSourceFileCheckbox" .checked=${this.deleteFiles} ?disabled=${this.isReadOnly}
            @change=${this.onDeleteFilesChange}>
            <p>Delete files from Source Location Path after workflow execution?</p>
          </jha-form-checkbox>
          <div class="info-tooltip">
            <jha-icon-information class="iconInfo" data-auto-id="deleteSourceFileInfo"></jha-icon-information>
            <span id="filePatternTooltip">
              Files in the Source Location Path are deleted after workflow execution only if the workflow contains at least
              one
              step
            </span>
          </div>
        </div>
        `
        : ""}
      
        <!-- Steps Table -->
        ${hasSteps
        ? html`
        <jha-table>
          ${this.renderTableHeader(canEdit)} ${this.steps.map((step) => this.renderStepRow(step, canEdit))}
        </jha-table>
        `
        : html`
        <jha-table> ${this.renderTableHeader(canEdit)} </jha-table>
        <div class="no-steps-message">
          <h3>No Steps Found</h3>
        </div>
        `}
      
        <!-- Add Step Button -->
        ${canEdit
        ? html`
        <div id="addStepContainer">
          <jha-tooltip left text="Add a new workflow step">
            <jha-button type="button" primary sync data-auto-id="addStepBtn" @click=${this.onAddStep}>
              <jha-icon-plus slot="icon-left"></jha-icon-plus>
              Add Workflow Step
            </jha-button>
          </jha-tooltip>
        </div>
        `
        : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "workflow-template-steps-view": WorkflowTemplateStepsView;
  }
}
